import clsx from 'clsx';
import { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import { FormControl, Form } from 'react-bootstrap';
import { useDebounce } from '../../../utils/AppFunction';
import { useInputSelectContext } from '../context';
import { InputConfig, InputProps } from '../types';
import Fuse from 'fuse.js';

const Input = ({
	inputConfig,
	fetchApi,
	getParams,
	urlData,
	getTableData,
	value,
	onChange,
	disabled,
	isLocalMode,
}: InputProps) => {
	const { selectedOption, setIsLoading, setOptions, setShow, initialOptions } =
		useInputSelectContext();

	const [inputValue, setInputValue] = useState<any>();

	const inputsRef = useRef<HTMLInputElement[]>([]);

	const searchParams = useDebounce(inputValue, 500);

	useEffect(() => {
		setInputValue(value);
	}, [value]);

	useEffect(() => {
		if (!inputConfig.length) return;

		const fieldValue = inputConfig.reduce((acc: { [key: string]: string }, field) => {
			acc[field.name] = selectedOption[field.name] || '';
			return acc;
		}, {});

		onChange(fieldValue);
	}, [selectedOption, inputConfig.length]);

	useEffect(() => {
		const hasInputFocus = inputsRef.current.some((input) =>
			input.contains(document.activeElement)
		);
		if (!hasInputFocus) return;

		const params = getParams(inputValue);

		isLocalMode ? searchLocalOptions(params.keyword) : getOptions(params);
	}, [searchParams, inputsRef, isLocalMode]);

	const getOptions = async (params: any) => {
		try {
			if (!fetchApi) return;

			setIsLoading(true);

			const response = await fetchApi(params);
			const data = getDataFromUrl(response, urlData);
			setOptions(getTableData ? getTableData(data) : data);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const getDataFromUrl = (data: any, urlData?: string) => {
		if (!urlData) return data;

		let tempData: Record<string, any> = { ...data };
		const paths = urlData.split('.');

		const result = paths.reduce((_, path) => {
			tempData = tempData[path];
			return tempData;
		}, {});

		return result;
	};

	const searchLocalOptions = (keyword: string) => {
		if (!keyword) {
			setOptions(initialOptions);
			return;
		}

		const fuse = new Fuse(initialOptions, {
			includeScore: true,
			shouldSort: true,
			includeMatches: false,
			useExtendedSearch: true,
			ignoreLocation: true,
			ignoreFieldNorm: true,
			fieldNormWeight: 1,
			threshold: 0.4,
			keys: inputConfig.reduce((acc: string[], item) => {
				acc.push(item.name);
				return acc;
			}, []),
		});
		const result = fuse.search(`'${keyword}`).map((record) => record.item);
		setOptions(result);
	};

	return (
		<>
			{inputConfig.map((field: InputConfig, index: number) => (
				<Fragment key={field.name}>
					<FormControl
						aria-label={field.label}
						className={clsx('customs-input', field.className, {
							'is-invalid': field.touched && field.error,
						})}
						value={inputValue?.[field.name] ?? ''}
						name={field.name}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							onChange({
								[e.target.name]: e.target.value,
							});
						}}
						onFocus={() => {
							setShow(true);
						}}
						ref={(element: HTMLInputElement) => {
							inputsRef.current[index] = element;
						}}
						onBlur={() => {
							field.setFieldTouched?.(field.name, true, true);
						}}
						disabled={disabled}
						{...field.config}
					/>
					<Form.Control.Feedback type="invalid" tooltip className="field-tooltip-error">
						{field.error}
					</Form.Control.Feedback>
				</Fragment>
			))}
		</>
	);
};

export default Input;
